/**
 * ProjectTableTab — Step 28 B1-B4 Upgrade
 *
 * Spreadsheet-like grid with:
 * - Bulk selection (checkbox, select-all, shift-click range)
 * - Bulk actions bar (status, assign, phase, delete)
 * - Inline editing for all key fields (B2)
 * - Column visibility + order persistence (B3)
 * - Grouping support (B4)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { formatCalendarDate } from '@/utils/calendarDate';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Table2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  User,
  AlertCircle,
  Plus,
  X,
  Trash2,
  ChevronDown,
  ChevronRight,
  Check,
  MoreVertical,
  Upload,
} from 'lucide-react';
import { useWorkspaceStore } from '@/state/workspace.store';
import {
  bulkUpdate,
  listTasks,
  createTask,
  updateTask,
  deleteTask as deleteTaskApi,
  type WorkTask,
  type WorkTaskStatus,
  type WorkTaskPriority,
  type TaskClosureKind,
} from '@/features/work-management/workTasks.api';
import { taskActionErrorMessage } from './taskActionErrorMessage';
import {
  WORK_TASK_TYPES,
  WORK_TASK_TYPE_COLORS,
} from '@/features/work-management/workTaskType.constants';
import { toast } from 'sonner';
import { notifyBulkSkipped } from '@/features/work-management/bulkUpdateSummary';
import { WorkItemDetailPanel } from '@/features/work-management/components/WorkItemDetailPanel';
import { ViewToolbar } from '../views/ViewToolbar';
import { calculateScheduleInfo, SCHEDULE_STATUS_CONFIG } from '@/features/work-management/utils/schedule-variance';
import { InlineLoadingState } from '@/components/ui/states';
import { listWorkspaceMembers, type WorkspaceMemberRow } from '@/features/workspaces/members/api';
import { apiClient } from '@/lib/api/client';
import { TaskClosureReasonDialog } from '@/features/work-management/components/TaskClosureReasonDialog';
import {
  cancelClosureBody,
  closureRequiredForPhase,
  deferralClosureBody,
  deferralDecision,
  isExecutionLockResetAckRequired,
  moveMenuPhases,
  phaseFromPlan,
  useGatesFromPlan,
  type ClosurePhase,
} from '@/features/work-management/taskClosure';
import { FilterBar, filtersFromParams, filtersToApiParams, isFilterActive, type FilterBarOptions, type TaskFilters } from '../components/FilterBar';
import {
  ActivitiesTaskRowMenu,
  type TaskConvertType,
  type TaskLinkRelation,
} from '../components/ActivitiesTaskRowMenu';
import { linkTaskDependency } from '../components/linkTaskDependency';
import { useEffectiveRole } from '@/utils/access/useEffectiveRole';
import { useProjectContext } from '../layout/ProjectPageLayout';
import { useRouteAction } from '@/features/projects/useRouteAction';
import { UnifiedWorkFieldsPanel } from '@/features/projects/fields/UnifiedWorkFieldsPanel';
import { AttributeCell } from '@/features/attributes/components/AttributeCell';
import {
  batchGetAttributeValues,
  upsertTaskAttributeValue,
} from '@/features/attributes/attributes.api';
import { useProjectAttributeColumns } from '@/features/attributes/useProjectAttributeColumns';
import { mapAttributeApiError, isAttributeTypeMismatch } from '@/features/attributes/mapAttributeApiError';
import {
  attributeColumnId,
  parseAttributeColumnId,
} from '@/features/attributes/attributes.types';
import { compareAttributeValues } from '@/features/attributes/attributeValue.utils';
import { TaskImportModal } from '@/features/importer/components/TaskImportModal';

function collectDescendantTaskIds(rootId: string, tasks: WorkTask[]): Set<string> {
  const descendants = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.pop()!;
    for (const t of tasks) {
      if (t.parentTaskId === id && !descendants.has(t.id)) {
        descendants.add(t.id);
        queue.push(t.id);
      }
    }
  }
  return descendants;
}

function parentTaskCandidatesFor(task: WorkTask, tasks: WorkTask[]): Array<{ id: string; title: string }> {
  const blocked = collectDescendantTaskIds(task.id, tasks);
  blocked.add(task.id);
  return tasks
    .filter((t) => !blocked.has(t.id))
    .map((t) => ({ id: t.id, title: t.title }));
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

type SortField = 'title' | 'status' | 'priority' | 'dueDate' | 'type' | 'createdAt';
type TableSortKey = SortField | `attr:${string}`;
type SortDir = 'asc' | 'desc';

const STATUS_OPTIONS: WorkTaskStatus[] = [
  'BACKLOG', 'TODO', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW', 'DONE', 'CANCELED',
];
const PRIORITY_OPTIONS: WorkTaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const TYPE_OPTIONS = [...WORK_TASK_TYPES];

const STATUS_COLORS: Record<string, string> = {
  BACKLOG: 'bg-slate-100 text-slate-700',
  TODO: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  BLOCKED: 'bg-red-100 text-red-700',
  IN_REVIEW: 'bg-purple-100 text-purple-700',
  DONE: 'bg-green-100 text-green-700',
  CANCELED: 'bg-slate-200 text-slate-500',
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-600',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  HIGH: 'bg-orange-100 text-orange-700',
  CRITICAL: 'bg-red-100 text-red-700',
};

/* ------------------------------------------------------------------ */
/*  Column config (B3)                                                 */
/* ------------------------------------------------------------------ */

interface ColumnDef {
  id: string;
  label: string;
  sortField?: SortField;
  width: string;
  visible: boolean;
}

const DEFAULT_COLUMNS: ColumnDef[] = [
  { id: 'title', label: 'Title', sortField: 'title', width: 'min-w-[260px]', visible: true },
  { id: 'status', label: 'Status', sortField: 'status', width: 'w-32', visible: true },
  { id: 'priority', label: 'Priority', sortField: 'priority', width: 'w-28', visible: true },
  { id: 'dueDate', label: 'Due Date', sortField: 'dueDate', width: 'w-32', visible: true },
  { id: 'startDate', label: 'Start Date', width: 'w-32', visible: false },
  { id: 'type', label: 'Type', sortField: 'type', width: 'w-24', visible: true },
  { id: 'assignee', label: 'Assignee', width: 'w-32', visible: true },
  { id: 'phase', label: 'Phase', width: 'w-32', visible: true },
  { id: 'tags', label: 'Tags', width: 'w-36', visible: false },
  { id: 'estimatedEffortHours', label: 'Est. Hours', width: 'w-24', visible: false },
  { id: 'remainingEffortHours', label: 'Rem. Hours', width: 'w-24', visible: false },
  { id: 'schedule', label: 'Schedule', width: 'w-20', visible: true },
];

/* ------------------------------------------------------------------ */
/*  Toolbar config                                                     */
/* ------------------------------------------------------------------ */

interface TableToolbarConfig {
  search?: string;
  groupBy?: string | null;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  visibleFields?: string[];
  showClosed?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ProjectTableTab: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { activeWorkspaceId } = useWorkspaceStore();
  const [searchParams] = useSearchParams();
  // Taxonomy §3.5 + §4 rows 19-23. Five tokens map to distinct affordances per
  // §3.5 row; all resolve identically today via §2.4 workspace-aware collapse
  // (Admin column OR Member column ✓; Viewer column ✗).
  // Migration note: replaces legacy `useWorkspacePermissions().canEditWork`.
  // `canEditWork` had a §5.5 bug (denied `delivery_owner`); the new tokens
  // correct this via the workspace-aware §2.4 collapse in `useEffectiveRole`.
  const { can } = useEffectiveRole();
  const { isReadOnly } = useProjectContext();
  const canCreateTask = can('task.create') && !isReadOnly;
  const canEditTask = can('task.edit') && !isReadOnly;
  const canDeleteTask = can('task.delete') && !isReadOnly;
  const canAssignTask = can('task.assign') && !isReadOnly;
  const canBulkUpdateTask = can('task.bulk.update') && !isReadOnly;

  const [tasks, setTasks] = useState<WorkTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [pendingDeleteTask, setPendingDeleteTask] = useState<WorkTask | null>(null);

  const [sortKey, setSortKey] = useState<TableSortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Toolbar config
  const [toolbarConfig, setToolbarConfig] = useState<TableToolbarConfig>({
    showClosed: false,
  });

  // Inline create
  const [showAddRow, setShowAddRow] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  // Bulk selection (B1)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);

  // Bulk action state
  const [bulkAction, setBulkAction] = useState<string | null>(null);
  const [bulkValue, setBulkValue] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Inline editing (B2) + Sprint 1 unified onCommit pipeline
  const [editingCell, setEditingCell] = useState<{ taskId: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [commitError, setCommitError] = useState<{ taskId: string; field: string; message: string } | null>(null);

  // Column visibility stays in this browser only. A saved-view API is not mounted.
  const [columns, setColumns] = useState<ColumnDef[]>(DEFAULT_COLUMNS);
  const [showFieldsPanel, setShowFieldsPanel] = useState(false);
  const attributePanelAnchorRef = useRef<HTMLButtonElement>(null);
  const {
    available: availableAttributes,
    visibleIds: visibleAttributeIds,
    visibleDefinitions: visibleAttributeDefinitions,
    loadFailed: attributesLoadFailed,
    toggleColumn: toggleAttributeColumn,
    onCreated: onAttributeCreated,
    retry: retryAttributes,
  } = useProjectAttributeColumns(activeWorkspaceId, projectId);
  const [attributeValues, setAttributeValues] = useState<Record<string, Record<string, unknown>>>({});

  // Grouping (B4)
  const [groups, setGroups] = useState<Array<{ key: string; label: string; count: number; completionPercent: number; items: WorkTask[] }> | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Members + Phases for bulk actions (Fix 2)
  const [members, setMembers] = useState<WorkspaceMemberRow[]>([]);
  const [phases, setPhases] = useState<ClosurePhase[]>([]);
  const [planUseGates, setPlanUseGates] = useState(false);
  const [closurePrompt, setClosurePrompt] = useState<{
    taskIds: string[];
    kind: TaskClosureKind;
    phaseId?: string;
    bulk?: boolean;
  } | null>(null);

  // Pagination (Sprint 1)
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 200;

  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);
  const tableTrailingColCount =
    visibleAttributeDefinitions.length + 1 + (canEditTask ? 1 : 0);

  // Sprint 1: FilterBar options and URL-synced filters
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const filterBarOptions: FilterBarOptions = useMemo(() => ({
    members,
    phases,
    statuses: STATUS_OPTIONS as unknown as string[],
    priorities: PRIORITY_OPTIONS as unknown as string[],
    types: TYPE_OPTIONS,
  }), [members, phases]);

  /* ---- Column visibility for this browser only ---- */
  useEffect(() => {
    if (!projectId) return;
    try {
      const saved = localStorage.getItem(`zephix-table-cols-${projectId}`);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) setColumns(parsed);
    } catch {
      /* keep the default columns */
    }
  }, [projectId]);

  /* ---- Batch-load attribute values for visible columns ---- */
  useEffect(() => {
    if (!activeWorkspaceId || visibleAttributeDefinitions.length === 0 || tasks.length === 0) {
      return;
    }
    const taskIds = tasks.map((t) => t.id);
    let cancelled = false;
    batchGetAttributeValues(taskIds, activeWorkspaceId)
      .then((batch) => {
        if (cancelled) return;
        setAttributeValues((prev) => {
          const next = { ...prev };
          for (const taskId of taskIds) {
            const fromBatch = batch[taskId];
            if (!fromBatch) continue;
            next[taskId] = { ...(next[taskId] ?? {}), ...fromBatch };
          }
          return next;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, tasks, visibleAttributeDefinitions]);

  useEffect(() => {
    if (!projectId) return;
    try {
      localStorage.setItem(`zephix-table-cols-${projectId}`, JSON.stringify(columns));
    } catch {
      /* private mode — columns stay in this session only */
    }
  }, [columns, projectId]);

  /* ---- Load members + phases for bulk actions ---- */

  useEffect(() => {
    if (!activeWorkspaceId) return;
    listWorkspaceMembers(activeWorkspaceId).then(setMembers).catch(() => {});
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!projectId || !activeWorkspaceId) return;
    apiClient
      .get(`/work/projects/${projectId}/plan`, {
        headers: { 'x-workspace-id': activeWorkspaceId },
      })
      .then((res) => {
        const plan = (res?.data as any)?.data ?? (res?.data as any);
        setPhases((plan?.phases ?? []).map((phase: { id: string; name?: string; sortOrder?: number; gate?: { definitionExists?: boolean } | null }) => phaseFromPlan(phase)));
        setPlanUseGates(useGatesFromPlan(plan) === true);
      })
      .catch(() => {});
  }, [projectId, activeWorkspaceId]);

  /* ---- Data loading ---- */

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);

      // Sprint 1: Build API params from URL filters
      const apiFilters = filtersToApiParams(filters);
      const baseParams = {
        projectId,
        limit: 200,
        ...apiFilters,
      };

      // Grouping not yet implemented - use regular listTasks
      const result = await listTasks(baseParams);
      setTasks(result.items.filter((t) => !t.deletedAt));
      setTotalCount(result.total);
      setGroups(null);
      // Clear selection when data changes (filters or reload)
      setSelectedIds(new Set());
    } catch (err: any) {
      console.error('Table: failed to load tasks', err);
      setError(err?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [projectId, toolbarConfig.groupBy, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** WM-B1: open detail panel when deep-linked via ?taskId= (Board, notifications). */
  useEffect(() => {
    const taskId =
      searchParams.get('taskId') ?? searchParams.get('task');
    if (taskId) {
      setSelectedTaskId(taskId);
    }
  }, [searchParams]);

  /* ---- Sorting ---- */

  const handleSort = (field: SortField) => {
    if (sortKey === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(field);
      setSortDir('asc');
    }
  };

  const handleAttributeSort = (definitionId: string) => {
    const key = `attr:${definitionId}` as TableSortKey;
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  /* ---- Filter + sort tasks ---- */

  const getFilteredSorted = (taskList: WorkTask[]) => {
    let filtered = taskList.filter((t) => {
      // Show Closed gate: hide DONE/CANCELED unless showClosed is on
      // OR the user explicitly included that status in the FilterBar
      const hasExplicitStatusFilter = filters.status && filters.status.length > 0;
      if (
        !toolbarConfig.showClosed &&
        !hasExplicitStatusFilter &&
        (t.status === 'DONE' || t.status === 'CANCELED')
      )
        return false;
      if (
        toolbarConfig.search &&
        !t.title.toLowerCase().includes(toolbarConfig.search.toLowerCase())
      )
        return false;
      // Sprint 1: Client-side filtering for multi-value filters not fully handled by backend
      if (filters.priority && filters.priority.length > 1 && !filters.priority.includes(t.priority))
        return false;
      if (filters.assigneeUserId && filters.assigneeUserId.length > 1 && !filters.assigneeUserId.includes(t.assigneeUserId || ''))
        return false;
      if (filters.phaseId && filters.phaseId.length > 1 && !filters.phaseId.includes(t.phaseId || ''))
        return false;
      if (filters.type && filters.type.length > 1 && !filters.type.includes(t.type || ''))
        return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey.startsWith('attr:')) {
        const defId = sortKey.slice(5);
        const def = visibleAttributeDefinitions.find((d) => d.id === defId);
        const av = attributeValues[a.id]?.[defId];
        const bv = attributeValues[b.id]?.[defId];
        const cmp = def ? compareAttributeValues(def.dataType, av, bv) : 0;
        return cmp * dir;
      }
      const av = (a as any)[sortKey] ?? '';
      const bv = (b as any)[sortKey] ?? '';
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  const sorted = useMemo(
    () => getFilteredSorted(tasks),
    [tasks, toolbarConfig, sortKey, sortDir, attributeValues, visibleAttributeDefinitions],
  );

  /* ---- Sprint 1: Unified onCommit pipeline ---- */
  /* All inline edits and bulk actions route through this single function.
   * 1. Optimistic update → local state
   * 2. Call updateTask
   * 3. Rollback on failure + show error banner
   */

  const onCommit = useCallback(async (
    taskId: string,
    field: string,
    value: string | string[] | number | null,
  ) => {
    // Client-side validation
    if (field === 'startDate' && value) {
      const task = tasks.find((t) => t.id === taskId);
      if (task?.dueDate && String(value) > task.dueDate.split('T')[0]) {
        setCommitError({ taskId, field, message: 'Start date cannot be after due date' });
        return;
      }
    }
    if (field === 'dueDate' && value) {
      const task = tasks.find((t) => t.id === taskId);
      if (task?.startDate && String(value) < task.startDate.split('T')[0]) {
        setCommitError({ taskId, field, message: 'Due date cannot be before start date' });
        return;
      }
    }
    if ((field === 'estimateHours' || field === 'remainingHours') && value !== null) {
      const num = Number(value);
      if (isNaN(num) || num < 0) {
        setCommitError({ taskId, field, message: 'Effort cannot be negative' });
        return;
      }
    }

    const currentTask = tasks.find((item) => item.id === taskId);
    if (currentTask && field === 'status' && value === 'CANCELED' && currentTask.status !== 'CANCELED') {
      if (closureRequiredForPhase(planUseGates, phases.find((phase) => phase.id === currentTask.phaseId))) {
        setClosurePrompt({ taskIds: [taskId], kind: 'CANCELLED' });
        setEditingCell(null);
        return;
      }
    }
    if (currentTask && field === 'phaseId' && typeof value === 'string' && value !== currentTask.phaseId) {
      const decision = deferralDecision(phases, currentTask, planUseGates);
      if (decision.required) {
        if (!decision.next || decision.next.id !== value) {
          setCommitError({
            taskId,
            field,
            message: decision.next
              ? `An unfinished task can only be deferred to ${decision.next.name}`
              : 'There is no next phase to defer this task into',
          });
          setEditingCell(null);
          return;
        }
        setClosurePrompt({ taskIds: [taskId], kind: 'DEFERRED', phaseId: value });
        setEditingCell(null);
        return;
      }
    }

    // Optimistic update
    const snapshot = tasks;
    setTasks((ts) =>
      ts.map((t) => (t.id === taskId ? { ...t, [field]: value } : t)),
    );
    setEditingCell(null);
    setCommitError(null);

    try {
      await updateTask(taskId, { [field]: value } as any);
    } catch (err: any) {
      // Rollback
      setTasks(snapshot);
      const msg = err?.response?.data?.message || err?.message || `Failed to update ${field}`;
      setCommitError({ taskId, field, message: msg });
    }
  }, [tasks]);

  const onCommitAttribute = useCallback(
    async (taskId: string, definitionId: string, value: unknown) => {
      const field = attributeColumnId(definitionId);
      const snapshot = attributeValues;
      setAttributeValues((prev) => {
        const taskVals = { ...(prev[taskId] ?? {}) };
        if (value === null || value === undefined || value === '') {
          delete taskVals[definitionId];
        } else {
          taskVals[definitionId] = value;
        }
        return { ...prev, [taskId]: taskVals };
      });
      setEditingCell(null);
      setCommitError(null);

      try {
        const def = availableAttributes.find((d) => d.id === definitionId);
        await upsertTaskAttributeValue(
          taskId,
          definitionId,
          value,
          activeWorkspaceId ?? undefined,
          def?.dataType,
        );
      } catch (err) {
        setAttributeValues(snapshot);
        const mapped = mapAttributeApiError(err);
        setCommitError({ taskId, field, message: mapped.message });
        if (!isAttributeTypeMismatch(err)) {
          toast.error(mapped.message);
        }
      }
    },
    [attributeValues, activeWorkspaceId, availableAttributes],
  );

  const startEdit = (taskId: string, field: string, currentValue: string) => {
    if (!canEditTask) return; // Taxonomy §3.5 / §4 row 20: Viewer cannot enter inline edit mode.
    setEditingCell({ taskId, field });
    setEditValue(currentValue);
    setCommitError(null);
  };

  /* ---- Inline create ---- */

  const handleInlineCreate = async () => {
    if (!projectId || !newTitle.trim() || creating) return;
    setCreating(true);
    try {
      const created = await createTask({ projectId, title: newTitle.trim() });
      setTasks((prev) => [...prev, created]);
      setNewTitle('');
      setShowAddRow(false);
      toast.success('Task created');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  /* ---- Bulk selection (B1) ---- */

  const toggleSelect = (taskId: string, shiftKey: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (shiftKey && lastClickedRef.current) {
        // Range selection
        const lastIdx = sorted.findIndex((t) => t.id === lastClickedRef.current);
        const curIdx = sorted.findIndex((t) => t.id === taskId);
        if (lastIdx >= 0 && curIdx >= 0) {
          const [start, end] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx];
          for (let i = start; i <= end; i++) {
            next.add(sorted[i].id);
          }
        }
      } else {
        if (next.has(taskId)) {
          next.delete(taskId);
        } else {
          next.add(taskId);
        }
      }

      lastClickedRef.current = taskId;
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sorted.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map((t) => t.id)));
    }
  };

  const handleDuplicateTask = useCallback(
    async (source: WorkTask) => {
      if (!projectId) return;
      try {
        const created = await createTask({
          projectId,
          title: `${source.title} (copy)`,
          phaseId: source.phaseId ?? undefined,
          assigneeUserId: source.assigneeUserId ?? undefined,
          dueDate: source.dueDate ?? undefined,
          priority: source.priority,
          description: source.description ?? undefined,
          tags: source.tags ?? undefined,
        });
        setTasks((prev) => [...prev, created]);
        toast.success('Task duplicated');
      } catch (err: unknown) {
        toast.error(taskActionErrorMessage(err, 'Failed to duplicate task'));
      }
    },
    [projectId],
  );

  const handleArchiveTask = useCallback(async (task: WorkTask) => {
    try {
      await updateTask(task.id, { archived: true });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setSelectedIds((prev) => {
        if (!prev.has(task.id)) return prev;
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
      toast.success('Task archived');
    } catch (err: unknown) {
      toast.error(taskActionErrorMessage(err, 'Failed to archive task'));
    }
  }, []);

  const handleMoveTaskToPhase = useCallback(async (taskId: string, phaseId: string) => {
    const current = tasks.find((item) => item.id === taskId);
    if (current) {
      const decision = deferralDecision(phases, current, planUseGates);
      if (decision.required) {
        if (!decision.next || decision.next.id !== phaseId) {
          toast.error(
            decision.next
              ? `An unfinished task can only be deferred to ${decision.next.name}`
              : 'There is no next phase to defer this task into',
          );
          return;
        }
        setClosurePrompt({ taskIds: [taskId], kind: 'DEFERRED', phaseId });
        return;
      }
    }
    const snapshot = tasks;
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, phaseId } : t)));
    try {
      await updateTask(taskId, { phaseId });
      toast.success('Task moved');
    } catch (err: unknown) {
      setTasks(snapshot);
      toast.error(taskActionErrorMessage(err, 'Failed to move task'));
    }
  }, [tasks, phases, planUseGates]);

  const handleConvertToSubtask = useCallback(
    async (task: WorkTask, parentTaskId: string) => {
      const snapshot = tasks;
      setTasks((ts) =>
        ts.map((t) => (t.id === task.id ? { ...t, parentTaskId } : t)),
      );
      try {
        await updateTask(task.id, { parentTaskId });
        toast.success('Task converted to subtask');
      } catch (err: unknown) {
        setTasks(snapshot);
        toast.error(taskActionErrorMessage(err, 'Failed to convert to subtask'));
      }
    },
    [tasks],
  );

  const handleLinkTask = useCallback(
    async (taskId: string, targetId: string, relation: TaskLinkRelation) => {
      if (await linkTaskDependency(taskId, targetId, relation)) {
        await loadData();
      }
    },
    [loadData],
  );

  const handleCopyTaskLink = useCallback(
    async (taskId: string) => {
      if (!projectId) return;
      const url = `${window.location.origin}/projects/${projectId}?taskId=${taskId}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      } catch {
        toast.error('Could not copy link');
      }
    },
    [projectId],
  );

  const handlePromoteTask = useCallback(
    async (task: WorkTask) => {
      const snapshot = tasks;
      setTasks((ts) =>
        ts.map((t) => (t.id === task.id ? { ...t, parentTaskId: null } : t)),
      );
      try {
        await updateTask(task.id, { parentTaskId: null });
        toast.success('Task promoted to standalone task');
      } catch {
        setTasks(snapshot);
        toast.error('Failed to promote task');
      }
    },
    [tasks],
  );

  const handleConvertTask = useCallback(
    async (task: WorkTask, type: TaskConvertType) => {
      if (type === 'meeting_note') {
        toast.message('Meeting notes coming in next update');
        return;
      }
      if (type === 'milestone') {
        const snapshot = tasks;
        setTasks((ts) =>
          ts.map((t) => (t.id === task.id ? { ...t, isMilestone: true } : t)),
        );
        try {
          await updateTask(task.id, { isMilestone: true });
          toast.success('Converted to milestone');
        } catch {
          setTasks(snapshot);
          toast.error('Failed to convert task');
        }
      }
    },
    [tasks],
  );

  const handleAddSubtask = useCallback(
    async (parent: WorkTask) => {
      if (!projectId) return;
      try {
        const created = await createTask({
          projectId,
          title: 'New subtask',
          parentTaskId: parent.id,
          phaseId: parent.phaseId ?? undefined,
        });
        setTasks((prev) => [...prev, created]);
        toast.success('Subtask created');
      } catch (err: unknown) {
        toast.error(taskActionErrorMessage(err, 'Failed to create subtask'));
      }
    },
    [projectId],
  );

  const executeDeleteTask = useCallback(async (task: WorkTask) => {
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    setSelectedIds((prev) => {
      if (!prev.has(task.id)) return prev;
      const next = new Set(prev);
      next.delete(task.id);
      return next;
    });
    try {
      await deleteTaskApi(task.id);
      toast.success('Task deleted');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Could not delete task';
      toast.error(msg);
      void loadData();
    }
  }, [loadData]);

  /* ---- Bulk actions (B1) ---- */

  // Follow-up B: structured error code classifier
  const classifyBulkError = (err: any): { code: string; label: string } => {
    const status = err?.response?.status || err?.status;
    if (status === 403) return { code: 'PERMISSION_DENIED', label: 'Permission denied' };
    if (status === 409) return { code: 'CONFLICT', label: 'Conflict' };
    if (status === 400) return { code: 'VALIDATION_FAILED', label: 'Validation failed' };
    return { code: 'SYSTEM_ERROR', label: 'System error' };
  };

  const [bulkError, setBulkError] = useState<{ failedCount: number; code: string; message: string } | null>(null);

  const handleBulkExecute = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (bulkAction === 'status' && bulkValue === 'CANCELED') {
      const needsReason = ids.some((id) => {
        const task = tasks.find((item) => item.id === id);
        if (!task || task.status === 'CANCELED') return false;
        return closureRequiredForPhase(planUseGates, phases.find((phase) => phase.id === task.phaseId));
      });
      if (needsReason) {
        setClosurePrompt({ taskIds: ids, kind: 'CANCELLED', bulk: true });
        return;
      }
    }
    if (bulkAction === 'not_applicable') {
      setClosurePrompt({ taskIds: ids, kind: 'NOT_APPLICABLE', bulk: true });
      return;
    }
    if (bulkAction === 'phase' && bulkValue) {
      const unfinishedGated = ids
        .map((id) => tasks.find((item) => item.id === id))
        .filter((task): task is WorkTask => Boolean(task))
        .filter((task) => deferralDecision(phases, task, planUseGates).required);
      if (unfinishedGated.length > 0) {
        const blocked = unfinishedGated.find((task) => {
          const decision = deferralDecision(phases, task, planUseGates);
          return !decision.required || !decision.next || decision.next.id !== bulkValue;
        });
        if (blocked) {
          const decision = deferralDecision(phases, blocked, planUseGates);
          toast.error(
            decision.required && decision.next
              ? `An unfinished task can only be deferred to ${decision.next.name}`
              : 'There is no next phase to defer this task into',
          );
          return;
        }
        setClosurePrompt({ taskIds: ids, kind: 'DEFERRED', phaseId: bulkValue, bulk: true });
        return;
      }
    }
    setBulkProcessing(true);
    setBulkError(null);
    let hadFailures = false;

    try {
      if (bulkAction === 'delete') {
        if (!confirm(`Delete ${ids.length} task${ids.length > 1 ? 's' : ''}?`)) {
          setBulkProcessing(false);
          return;
        }
        const results = await Promise.allSettled(ids.map((id) => deleteTaskApi(id)));
        const successIds = new Set<string>();
        const failedIds: string[] = [];
        let primaryError: { code: string; label: string } | null = null;

        results.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            successIds.add(ids[i]);
          } else {
            failedIds.push(ids[i]);
            if (!primaryError) primaryError = classifyBulkError(r.reason);
          }
        });
        hadFailures = failedIds.length > 0;

        if (successIds.size > 0) {
          setTasks((prev) => prev.filter((t) => !successIds.has(t.id)));
          toast.success(`Deleted ${successIds.size} task${successIds.size > 1 ? 's' : ''}`);
        }
        if (hadFailures) {
          console.error('Bulk delete failed IDs:', failedIds);
          const errInfo = primaryError || { code: 'SYSTEM_ERROR', label: 'System error' };
          setBulkError({
            failedCount: failedIds.length,
            code: errInfo.code,
            message: `${failedIds.length} task${failedIds.length > 1 ? 's' : ''} failed to delete — ${errInfo.code}: ${errInfo.label}`,
          });
        }
        // Keep failed IDs selected, clear only successful ones
        setSelectedIds((prev) => {
          const next = new Set(prev);
          successIds.forEach((id) => next.delete(id));
          return next;
        });
      } else {
        // Use atomic bulk API (PATCH /work/tasks/actions/bulk-update)
        const bulkInput: any = { taskIds: ids };
        if (bulkAction === 'status') bulkInput.status = bulkValue;
        if (bulkAction === 'priority') bulkInput.priority = bulkValue;
        if (bulkAction === 'assignee') bulkInput.assigneeUserId = bulkValue || null;
        if (bulkAction === 'unassign') bulkInput.assigneeUserId = null;
        // Phase move stays as N+1 since bulk API doesn't support phaseId
        if (bulkAction === 'phase') {
          const phaseResults = await Promise.allSettled(
            ids.map((id) =>
              updateTask(id, { phaseId: bulkValue ? bulkValue : undefined }),
            ),
          );
          const phaseSuccess = phaseResults.filter((r) => r.status === 'fulfilled').length;
          if (phaseSuccess > 0) {
            toast.success(`Moved ${phaseSuccess} task${phaseSuccess > 1 ? 's' : ''}`);
            const refreshed = await listTasks({ projectId });
            setTasks(refreshed.items);
          }
          const phaseFailed = phaseResults.filter((r) => r.status === 'rejected').length;
          hadFailures = phaseFailed > 0;
          if (hadFailures) {
            setBulkError({
              failedCount: phaseFailed,
              code: 'PHASE_MOVE_FAILED',
              message: `${phaseFailed} task${phaseFailed > 1 ? 's' : ''} failed to move`,
            });
          }
        } else {
          // Atomic bulk update for status, priority, assign, unassign
          try {
            const result = await bulkUpdate(bulkInput);
            if (!notifyBulkSkipped(result)) {
              toast.success(`Updated ${result.updated} task${result.updated > 1 ? 's' : ''}`);
            }
            const refreshed = await listTasks({ projectId });
            setTasks(refreshed.items);
            setSelectedIds(new Set());
          } catch (bulkErr: any) {
            hadFailures = true;
            const errInfo = classifyBulkError(bulkErr);
            setBulkError({
              failedCount: ids.length,
              code: errInfo.code,
              message: bulkErr?.response?.data?.message || errInfo.label,
            });
          }
        }
      }

      // Only reset bulk action panel if all succeeded
      if (!hadFailures) {
        setBulkAction(null);
        setBulkValue('');
      }
    } catch (err: any) {
      setBulkError({
        failedCount: ids.length,
        code: 'SYSTEM_ERROR',
        message: `Bulk action failed — SYSTEM_ERROR: ${err?.message || 'Unknown error'}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  /* ---- Column visibility (B3) ---- */

  const toggleColumn = (colId: string) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === colId ? { ...c, visible: !c.visible } : c)),
    );
  };

  /* ---- Grouping (B4) ---- */

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ---- Starter tasks (A2 / Fix 4: server-side with audit) ---- */

  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const importMenuRef = useRef<HTMLDivElement>(null);
  // Project menu deep links (PROJECT-MENU-1): only offered to a caller who can edit tasks here.
  useRouteAction('import', !!projectId && !!activeWorkspaceId && canEditTask, () =>
    setImportModalOpen(true),
  );
  useRouteAction('custom-fields', canEditTask, () => setShowFieldsPanel(true));

  useEffect(() => {
    if (!importMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) {
        setImportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [importMenuOpen]);

  /* ---- Sort icon helper ---- */

  const SortIcon = ({ field }: { field: TableSortKey }) => {
    if (sortKey !== field)
      return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-indigo-600" />
    ) : (
      <ArrowDown className="h-3 w-3 text-indigo-600" />
    );
  };

  /* ---- Render cell — Sprint 1 complete set ---- */

  const memberNameMap = useMemo(() => {
    const m = new Map<string, string>();
    members.forEach((mb) => m.set(mb.userId, mb.name || mb.email));
    return m;
  }, [members]);

  const phaseNameMap = useMemo(() => {
    const m = new Map<string, string>();
    phases.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [phases]);

  const renderCell = (task: WorkTask, colId: string) => {
    const attrDefId = parseAttributeColumnId(colId);
    if (attrDefId) {
      const def = visibleAttributeDefinitions.find((d) => d.id === attrDefId);
      if (!def) return null;
      const value = attributeValues[task.id]?.[attrDefId];
      const isEditing = editingCell?.taskId === task.id && editingCell?.field === colId;
      const hasError = commitError?.taskId === task.id && commitError?.field === colId;
      return (
        <AttributeCell
          definition={def}
          value={value}
          canEdit={canEditTask}
          isEditing={isEditing}
          error={hasError ? commitError!.message : null}
          onStartEdit={() => startEdit(task.id, colId, '')}
          onCommit={(v) => void onCommitAttribute(task.id, attrDefId, v)}
          onCancel={() => setEditingCell(null)}
        />
      );
    }

    const isEditing = editingCell?.taskId === task.id && editingCell?.field === colId;
    const hasError = commitError?.taskId === task.id && commitError?.field === colId;
    const errorBorder = hasError ? ' ring-1 ring-red-400' : '';

    switch (colId) {
      case 'title':
        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCommit(task.id, 'title', editValue);
                if (e.key === 'Escape') setEditingCell(null);
              }}
              onBlur={() => onCommit(task.id, 'title', editValue)}
              onFocus={(e) => e.currentTarget.select()}
              className={`w-full text-sm px-1 py-0.5 border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-sm font-medium text-slate-900 truncate block max-w-[400px] cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); startEdit(task.id, 'title', task.title); }}
          >
            {task.title}
          </span>
        );

      case 'status':
        return (
          <select
            value={task.status}
            onChange={(e) => onCommit(task.id, 'status', e.target.value)}
            disabled={!canEditTask}
            className={`text-xs px-2 py-0.5 rounded font-medium border-0 ${canEditTask ? 'cursor-pointer' : 'cursor-default opacity-80'} ${STATUS_COLORS[task.status] || 'bg-slate-100 text-slate-700'}`}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        );

      case 'priority':
        return (
          <select
            value={task.priority}
            onChange={(e) => onCommit(task.id, 'priority', e.target.value)}
            disabled={!canEditTask}
            className={`text-xs px-2 py-0.5 rounded font-medium border-0 ${canEditTask ? 'cursor-pointer' : 'cursor-default opacity-80'} ${PRIORITY_COLORS[task.priority] || 'bg-slate-100 text-slate-600'}`}
          >
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        );

      case 'dueDate':
        if (isEditing) {
          return (
            <input
              type="date"
              value={editValue}
              onChange={(e) => onCommit(task.id, 'dueDate', e.target.value || null)}
              onBlur={() => setEditingCell(null)}
              className={`text-xs border border-indigo-300 rounded px-1 py-0.5 focus:outline-none${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-sm text-slate-600 cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); startEdit(task.id, 'dueDate', task.dueDate?.split('T')[0] || ''); }}
          >
            {task.dueDate ? formatCalendarDate(task.dueDate) : <span className="text-slate-400">—</span>}
          </span>
        );

      case 'startDate':
        if (isEditing) {
          return (
            <input
              type="date"
              value={editValue}
              onChange={(e) => onCommit(task.id, 'startDate', e.target.value || null)}
              onBlur={() => setEditingCell(null)}
              className={`text-xs border border-indigo-300 rounded px-1 py-0.5 focus:outline-none${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-sm text-slate-600 cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); startEdit(task.id, 'startDate', task.startDate?.split('T')[0] || ''); }}
          >
            {task.startDate ? formatCalendarDate(task.startDate) : <span className="text-slate-400">—</span>}
          </span>
        );

      case 'type':
        return (
          <select
            value={task.type || 'TASK'}
            onChange={(e) => onCommit(task.id, 'type', e.target.value)}
            disabled={!canEditTask}
            className={`text-xs px-2 py-0.5 rounded font-medium border-0 uppercase ${canEditTask ? 'cursor-pointer' : 'cursor-default opacity-80'} ${WORK_TASK_TYPE_COLORS[(task.type || 'TASK') as keyof typeof WORK_TASK_TYPE_COLORS] || 'bg-slate-100 text-slate-700'}`}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
        );

      case 'assignee':
        return (
          <select
            value={task.assigneeUserId || ''}
            onChange={(e) => onCommit(task.id, 'assigneeUserId', e.target.value || null)}
            disabled={!canAssignTask}
            className={`text-xs px-1 py-0.5 rounded bg-transparent border-0 text-slate-600 max-w-[120px] truncate ${canAssignTask ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
          >
            <option value="">Unassigned</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>{m.name || m.email}</option>
            ))}
          </select>
        );

      case 'phase':
        return (
          <select
            value={task.phaseId || ''}
            onChange={(e) => onCommit(task.id, 'phaseId', e.target.value || null)}
            disabled={!canEditTask}
            className={`text-xs px-1 py-0.5 rounded bg-transparent border-0 text-slate-600 max-w-[120px] truncate ${canEditTask ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
          >
            <option value="">No phase</option>
            {(() => {
              const offered = moveMenuPhases(phases, task, planUseGates);
              const choices = offered.deferralBlocked
                ? phases.filter((phase) => phase.id === task.phaseId)
                : phases.filter(
                    (phase) =>
                      phase.id === task.phaseId ||
                      offered.phases.some((item) => item.id === phase.id),
                  );
              return choices.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ));
            })()}
          </select>
        );

      case 'tags':
        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const tagArr = editValue.split(',').map((t) => t.trim()).filter(Boolean);
                  onCommit(task.id, 'tags', tagArr.length > 0 ? tagArr : null);
                }
                if (e.key === 'Escape') setEditingCell(null);
              }}
              onBlur={() => {
                const tagArr = editValue.split(',').map((t) => t.trim()).filter(Boolean);
                onCommit(task.id, 'tags', tagArr.length > 0 ? tagArr : null);
              }}
              placeholder="tag1, tag2..."
              className={`w-full text-xs px-1 py-0.5 border border-indigo-300 rounded focus:outline-none${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-xs text-slate-500 truncate block max-w-[140px] cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); startEdit(task.id, 'tags', task.tags?.join(', ') || ''); }}
          >
            {task.tags && task.tags.length > 0 ? task.tags.join(', ') : <span className="text-slate-400">—</span>}
          </span>
        );

      case 'estimatedEffortHours':
        if (isEditing) {
          return (
            <input
              type="number"
              min="0"
              step="0.5"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCommit(task.id, 'estimateHours', editValue ? Number(editValue) : null);
                if (e.key === 'Escape') setEditingCell(null);
              }}
              onBlur={() => onCommit(task.id, 'estimateHours', editValue ? Number(editValue) : null)}
              className={`w-16 text-xs px-1 py-0.5 border border-indigo-300 rounded focus:outline-none${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-xs text-slate-600 cursor-text"
            onDoubleClick={(e) => {
              e.stopPropagation();
              startEdit(task.id, 'estimatedEffortHours', task.estimateHours != null ? String(task.estimateHours) : '');
            }}
          >
            {task.estimateHours != null ? `${task.estimateHours}h` : <span className="text-slate-400">—</span>}
          </span>
        );

      case 'remainingEffortHours':
        if (isEditing) {
          return (
            <input
              type="number"
              min="0"
              step="0.5"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCommit(task.id, 'remainingHours', editValue ? Number(editValue) : null);
                if (e.key === 'Escape') setEditingCell(null);
              }}
              onBlur={() => onCommit(task.id, 'remainingHours', editValue ? Number(editValue) : null)}
              className={`w-16 text-xs px-1 py-0.5 border border-indigo-300 rounded focus:outline-none${errorBorder}`}
              autoFocus
            />
          );
        }
        return (
          <span
            className="text-xs text-slate-600 cursor-text"
            onDoubleClick={(e) => {
              e.stopPropagation();
              startEdit(task.id, 'remainingEffortHours', task.remainingHours != null ? String(task.remainingHours) : '');
            }}
          >
            {task.remainingHours != null ? `${task.remainingHours}h` : <span className="text-slate-400">—</span>}
          </span>
        );

      case 'schedule': {
        const sched = calculateScheduleInfo({
          startDate: task.startDate,
          dueDate: task.dueDate,
          actualStartDate: task.actualStartAt ?? null,
          actualEndDate: task.actualEndAt ?? null,
        });
        if (sched.plannedDurationDays == null) return <span className="text-xs text-slate-400">—</span>;
        const cfg = SCHEDULE_STATUS_CONFIG[sched.status];
        return (
          <span
            title={`${cfg.label}${sched.endVarianceDays != null ? ` (${sched.endVarianceDays > 0 ? '+' : ''}${sched.endVarianceDays}d)` : ''}`}
            className="inline-flex items-center gap-1"
          >
            <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
            <span className={`text-[10px] font-medium ${cfg.color.split(' ')[0]}`}>
              {sched.endVarianceDays != null
                ? `${sched.endVarianceDays > 0 ? '+' : ''}${sched.endVarianceDays}d`
                : cfg.label}
            </span>
          </span>
        );
      }

      default:
        return null;
    }
  };

  /* ---- Render row (memoized for performance) ---- */

  const TaskRow = React.memo(({ task }: { task: WorkTask }) => {
    const isSelected = selectedIds.has(task.id);
    const parentCandidates = parentTaskCandidatesFor(task, tasks);
    return (
      <tr
        className={`group transition-colors cursor-pointer ${isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
        onClick={() => setSelectedTaskId(task.id)}
      >
        {/* Checkbox — only for bulk-update-capable users (taxonomy §3.5) */}
        {canBulkUpdateTask && (
        <td className="px-2 py-1.5 w-10" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => toggleSelect(task.id, (e.nativeEvent as MouseEvent).shiftKey)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            data-testid={`task-checkbox-${task.id}`}
          />
        </td>
        )}

        {/* Dynamic columns */}
        {visibleColumns.map((col) => (
          <td
            key={col.id}
            className="px-3 py-1.5"
            onClick={(e) => {
              if (['status', 'priority', 'type', 'assignee', 'phase'].includes(col.id)) e.stopPropagation();
            }}
          >
            {renderCell(task, col.id)}
          </td>
        ))}

        {visibleAttributeDefinitions.map((def) => (
          <td
            key={def.id}
            className="px-3 py-1.5 min-w-[120px]"
            onClick={(e) => e.stopPropagation()}
          >
            {renderCell(task, attributeColumnId(def.id))}
          </td>
        ))}

        <td className="w-8 px-1" aria-hidden />

        {canEditTask && (
          <td className="px-2 py-1.5 w-10" onClick={(e) => e.stopPropagation()}>
            <ActivitiesTaskRowMenu
              taskId={task.id}
              taskTitle={task.title}
              isSubtask={!!task.parentTaskId}
              currentPhaseId={task.phaseId}
              phases={moveMenuPhases(phases, task, planUseGates).phases}
              deferralBlocked={moveMenuPhases(phases, task, planUseGates).deferralBlocked}
              onMarkNotApplicable={
                closureRequiredForPhase(planUseGates, phases.find((phase) => phase.id === task.phaseId))
                  ? () => setClosurePrompt({ taskIds: [task.id], kind: 'NOT_APPLICABLE' })
                  : undefined
              }
              parentTaskCandidates={parentCandidates}
              onOpen={() => setSelectedTaskId(task.id)}
              onRename={() => startEdit(task.id, 'title', task.title)}
              onAddSubtask={() => void handleAddSubtask(task)}
              onPromoteToTask={
                task.parentTaskId ? () => void handlePromoteTask(task) : undefined
              }
              onMoveToPhase={(phaseId) => void handleMoveTaskToPhase(task.id, phaseId)}
              onConvertTo={(type) => void handleConvertTask(task, type)}
              onConvertToSubtask={(parentId) => void handleConvertToSubtask(task, parentId)}
              onLinkTo={(relation, targetId) =>
                void handleLinkTask(task.id, targetId, relation)
              }
              onDuplicate={() => void handleDuplicateTask(task)}
              onCopyLink={() => void handleCopyTaskLink(task.id)}
              onRequestDelete={() => setPendingDeleteTask(task)}
            />
          </td>
        )}
      </tr>
    );
  });

  const renderRow = (task: WorkTask) => <TaskRow key={task.id} task={task} />;

  /* ---- Render ---- */

  if (!projectId || !activeWorkspaceId) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <p>Select a workspace and project to view the table.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="table-loading">
        <InlineLoadingState message="Loading table..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="table-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Error loading table
          </p>
          <p className="text-sm mt-1">{error}</p>
          <button onClick={loadData} className="mt-2 text-sm text-red-700 underline">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="table-root">
      {/* ─── Consolidated Execution Toolbar ─── */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <Table2 className="h-5 w-5 text-slate-700" />
        <h2 className="text-lg font-semibold text-slate-900">Table</h2>
        <span className="text-sm text-slate-500">{sorted.length} tasks</span>

        {/* Search + Show Closed from ViewToolbar */}
        <ViewToolbar
          viewType="table"
          config={toolbarConfig}
          onChange={(partial) => setToolbarConfig((prev) => ({ ...prev, ...partial }))}
          className="ml-2"
        />

        {/* Column visibility moved to unified header "+" fields panel (Phase 1). */}

        {canCreateTask && (
          <button
            onClick={() => setShowAddRow(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-md"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Task
          </button>
        )}

        {canCreateTask && (
          <div ref={importMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setImportMenuOpen((v) => !v)}
              aria-expanded={importMenuOpen}
              aria-haspopup="menu"
              aria-label="Table actions" title="Table actions"
              data-testid="table-toolbar-overflow"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <MoreVertical className="h-4 w-4" aria-hidden />
            </button>
            {importMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-1 min-w-[180px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  data-testid="table-import-tasks"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => {
                    setImportMenuOpen(false);
                    setImportModalOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                  Import tasks
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* ─── Filters (URL-param based, real data) ─── */}
      <FilterBar options={filterBarOptions} className="mb-3" />

      {/* ─── Bulk Action Bar (B1) — taxonomy §3.5 task.bulk.update ─── */}
      {canBulkUpdateTask && selectedIds.size > 0 && (
        <div
          className="sticky top-0 z-10 mb-3 flex items-center gap-3 rounded-lg bg-indigo-50 border border-indigo-200 px-4 py-2.5"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium text-indigo-800">
            {selectedIds.size} selected
          </span>

          <select
            value={bulkAction || ''}
            onChange={(e) => { setBulkAction(e.target.value || null); setBulkValue(''); }}
            className="text-xs border rounded px-2 py-1 bg-white"
          >
            <option value="">Choose action...</option>
            <option value="status">Change Status</option>
            <option value="priority">Change Priority</option>
            <option value="assignee">Assign User</option>
            <option value="unassign">Unassign</option>
            {phases.length > 0 && <option value="phase">Move to Phase</option>}
            {planUseGates && <option value="not_applicable">Mark not applicable</option>}
            {/* Taxonomy §3.5 / §4 row 21 — task.delete callsite. Today resolves
                identically to canBulkUpdateTask, but kept as a distinct gate so
                future nuance (e.g., "Member can edit but not delete") only
                touches the hook, not this file. */}
            {canDeleteTask && <option value="delete">Delete</option>}
          </select>

          {bulkAction === 'status' && (
            <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
              <option value="">Select status...</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          )}

          {bulkAction === 'priority' && (
            <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
              <option value="">Select priority...</option>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          )}

          {bulkAction === 'assignee' && (
            <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
              <option value="">Select user...</option>
              {members.map((m) => <option key={m.userId} value={m.userId}>{m.name || m.email}</option>)}
            </select>
          )}

          {bulkAction === 'phase' && (
            <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
              <option value="">Select phase...</option>
              {(() => {
                const selected = tasks.filter((task) => selectedIds.has(task.id));
                const nextIds = new Set(
                  selected
                    .map((task) => deferralDecision(phases, task, planUseGates))
                    .filter((decision) => decision.required)
                    .map((decision) => (decision.required ? decision.next?.id : undefined))
                    .filter((id): id is string => Boolean(id)),
                );
                const choices = nextIds.size > 0 ? phases.filter((phase) => nextIds.has(phase.id)) : phases;
                return choices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>);
              })()}
            </select>
          )}

          <button
            onClick={handleBulkExecute}
            disabled={bulkProcessing || !bulkAction || (!['delete', 'unassign', 'not_applicable'].includes(bulkAction) && !bulkValue)}
            className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {bulkProcessing ? 'Processing...' : 'Apply'}
          </button>

          <button
            onClick={() => { setSelectedIds(new Set()); setBulkAction(null); }}
            className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      {/* ─── Error banner (Sprint 1: single banner for commit errors) ─── */}
      {commitError && (
        <div className="mb-2 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" data-testid="commit-error-banner">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{commitError.message}</span>
          <button onClick={() => setCommitError(null)} className="ml-auto text-red-500 hover:text-red-700" aria-label="Dismiss error" title="Dismiss error"><X className="h-3 w-3" /></button>
        </div>
      )}

      {/* ─── Bulk error banner (Follow-up B: deterministic error codes) ─── */}
      {bulkError && (
        <div className="mb-2 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="bulk-error-banner">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="font-medium">[{bulkError.code}]</span>
          <span>{bulkError.message}</span>
          <span className="text-amber-600">({bulkError.failedCount} failed task{bulkError.failedCount > 1 ? 's' : ''} remain selected)</span>
          <button onClick={() => setBulkError(null)} className="ml-auto text-amber-600 hover:text-amber-800" aria-label="Dismiss error" title="Dismiss error"><X className="h-3 w-3" /></button>
        </div>
      )}

      {/* ─── Table ─── */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {/* Select all checkbox — taxonomy §3.5 task.bulk.update */}
              {canBulkUpdateTask && (
              <th className="px-2 py-2 w-10">
                <input
                  type="checkbox"
                  checked={sorted.length > 0 && selectedIds.size === sorted.length}
                  onChange={toggleSelectAll}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  data-testid="select-all-checkbox"
                />
              </th>
              )}
              {visibleColumns.map((col) => (
                <th
                  key={col.id}
                  className={`px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider select-none ${col.width} ${col.sortField ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                  onClick={() => col.sortField && handleSort(col.sortField)}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortField && <SortIcon field={col.sortField} />}
                  </span>
                </th>
              ))}
              {visibleAttributeDefinitions.map((def) => (
                <th
                  key={def.id}
                  className="min-w-[120px] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleAttributeSort(def.id)}
                  data-testid={`table-attr-header-${def.id}`}
                >
                  <span className="flex items-center gap-1">
                    {def.label}
                    <SortIcon field={`attr:${def.id}`} />
                  </span>
                </th>
              ))}
              <th className="relative w-8 px-1 py-2">
                <button
                  ref={attributePanelAnchorRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFieldsPanel((v) => !v);
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded hover:bg-slate-200 text-slate-600"
                  aria-label="Add fields" title="Add fields"
                  aria-expanded={showFieldsPanel}
                  data-testid="attribute-column-add-btn"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </th>
              {canEditTask && (
                <th className="w-10 px-2 py-2" aria-label="Row actions" />
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {/* Grouped view (B4) */}
            {groups && !toolbarConfig.search ? (
              groups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.key);
                const groupTasks = getFilteredSorted(group.items);
                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className="bg-slate-50 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleGroupCollapse(group.key)}
                    >
                      <td colSpan={visibleColumns.length + tableTrailingColCount} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-slate-500" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
                          <span className="text-xs font-semibold text-slate-700 uppercase">{group.label}</span>
                          <span className="text-xs text-slate-500">{group.count} tasks</span>
                          <div className="flex-1" />
                          <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${group.completionPercent}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-500">{group.completionPercent}%</span>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && groupTasks.map((task) => renderRow(task))}
                  </React.Fragment>
                );
              })
            ) : sorted.length === 0 && !showAddRow ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + tableTrailingColCount}
                  className="text-center py-12"
                >
                  <p className="text-sm text-slate-500 mb-1">No tasks found</p>
                  <p className="text-xs text-slate-400 mb-4">{canCreateTask ? 'Create a task to start this list' : 'No tasks match the current filters'}</p>
                  {canCreateTask && (
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setShowAddRow(true)}
                      className="px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50"
                    >
                      <Plus className="h-3 w-3 inline mr-1" />
                      Add Task
                    </button>
                  </div>
                  )}
                </td>
              </tr>
            ) : (
              sorted.map((task) => renderRow(task))
            )}

            {/* Inline add row */}
            {showAddRow && (
              <tr className="bg-indigo-50/50">
                <td className="px-3 py-2" colSpan={visibleColumns.length + tableTrailingColCount}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineCreate();
                        if (e.key === 'Escape') { setShowAddRow(false); setNewTitle(''); }
                      }}
                      placeholder="New task title..."
                      className="flex-1 text-sm px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      autoFocus
                      disabled={creating}
                    />
                    <button
                      onClick={handleInlineCreate}
                      disabled={creating || !newTitle.trim()}
                      className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {creating ? '...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddRow(false); setNewTitle(''); }}
                      className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination indicator */}
      {totalCount > PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-between px-1">
          <span className="text-xs text-slate-500">
            Showing {Math.min(sorted.length, PAGE_SIZE)} of {totalCount} tasks
          </span>
        </div>
      )}

      {showFieldsPanel && activeWorkspaceId ? (
        <UnifiedWorkFieldsPanel
          anchorRef={attributePanelAnchorRef}
          onClose={() => setShowFieldsPanel(false)}
          properties={{
            mode: 'table',
            columns,
            onToggleColumn: toggleColumn,
          }}
          customFields={{
            available: availableAttributes,
            visibleIds: visibleAttributeIds,
            onToggleColumn: (id, visible) => void toggleAttributeColumn(id, visible),
            onCreated: (def) => void onAttributeCreated(def),
            workspaceId: activeWorkspaceId,
            loadFailed: attributesLoadFailed,
            onRetry: retryAttributes,
          }}
        />
      ) : null}

      {pendingDeleteTask && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="table-delete-task-dialog-title"
          data-testid="table-delete-task-dialog"
        >
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3
              id="table-delete-task-dialog-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              Delete this task?
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDeleteTask(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const task = pendingDeleteTask;
                  setPendingDeleteTask(null);
                  if (task) void executeDeleteTask(task);
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {closurePrompt ? (
        <TaskClosureReasonDialog
          kind={closurePrompt.kind}
          taskTitle={
            closurePrompt.taskIds.length === 1
              ? tasks.find((task) => task.id === closurePrompt.taskIds[0])?.title ?? 'Task'
              : `${closurePrompt.taskIds.length} tasks`
          }
          onCancel={() => setClosurePrompt(null)}
          onConfirm={async (reason, options) => {
            const prompt = closurePrompt;
            if (prompt.kind === 'DEFERRED' && prompt.phaseId) {
              try {
                for (const id of prompt.taskIds) {
                  const task = tasks.find((item) => item.id === id);
                  const required = Boolean(task && deferralDecision(phases, task, planUseGates).required);
                  const body = required
                    ? deferralClosureBody(prompt.phaseId, reason)
                    : { phaseId: prompt.phaseId };
                  await updateTask(
                    id,
                    options?.acknowledgeStatusReset === true
                      ? { ...body, acknowledgeStatusReset: true }
                      : body,
                  );
                }
                if (projectId) {
                  const refreshed = await listTasks({ projectId });
                  setTasks(refreshed.items);
                }
                setClosurePrompt(null);
              } catch (err) {
                if (isExecutionLockResetAckRequired(err)) throw err;
                setClosurePrompt(null);
              }
              return;
            }
            setClosurePrompt(null);
            if (prompt.kind !== 'CANCELLED' && prompt.kind !== 'NOT_APPLICABLE') return;
            notifyBulkSkipped(
              await bulkUpdate({ taskIds: prompt.taskIds, ...cancelClosureBody(prompt.kind, reason) }),
            );
            if (projectId) {
              const refreshed = await listTasks({ projectId });
              setTasks(refreshed.items);
            }
            setSelectedIds(new Set());
          }}
        />
      ) : null}

      {selectedTaskId && projectId && activeWorkspaceId && (
        <WorkItemDetailPanel
          taskId={selectedTaskId}
          workspaceId={activeWorkspaceId}
          projectId={projectId}
          onClose={() => setSelectedTaskId(null)}
        />
      )}

      {importModalOpen && projectId && activeWorkspaceId ? (
        <TaskImportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          projectId={projectId}
          workspaceId={activeWorkspaceId}
          onImportComplete={() => void loadData()}
        />
      ) : null}
    </div>
  );
};

export default ProjectTableTab;
